import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUI } from '../../contexts/UIContext';
import Logo from '../../components/common/Logo';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { LegalModal, TermsContent, PrivacyContent } from '../../components/legal';
import { validateForm, signUpValidationSchema, getPasswordStrength } from '../../utils/formValidation';
import { organizationValidationAPI, organizationUtils } from '../../api/organizationValidation';
import {
  CheckCircleIcon,
  UsersIcon,
  ChartBarIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const SignUpPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signUp, loading: authLoading } = useAuth();
  const { showNotification } = useUI();

  // Get plan and source from URL parameters. Only 'sparvi' is a valid
  // public plan today; any other value (including 'free' from old links)
  // falls back to 'sparvi' so the user lands in the 14-day trial flow.
  const planParam = searchParams.get('plan');
  const initialPlan = planParam === 'sparvi' ? 'sparvi' : 'sparvi';
  const source = searchParams.get('source');

  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Inline submit error. The toast layer (<Notifications />) lives in
  // MainLayout, which only wraps authenticated routes — the signup page
  // sits outside it, so showNotification() calls go nowhere. Render the
  // error directly on the form instead.
  const [submitError, setSubmitError] = useState(null);

  // Legal modal state: null | 'terms' | 'privacy'
  const [legalModal, setLegalModal] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Plan Selection
    selectedPlan: initialPlan,
    
    // Step 2: Account Details
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    
    // Step 3: Organization Setup
    organizationName: '',
    teamSize: '',
    useCase: '',
    agreeToTerms: false
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Organization validation state
  const [organizationValidation, setOrganizationValidation] = useState({
    isChecking: false,
    isValid: true,
    conflict: null,
    message: null
  });

  // Email domain validation state
  const [emailDomainValidation, setEmailDomainValidation] = useState({
    isChecking: false,
    hasConflict: false,
    organizationName: null,
    adminContact: null,
    message: null
  });

  // Plan configurations. Single Sparvi plan with 14-day free trial. The
  // legacy 4-tier infrastructure remains in the backend but is not exposed
  // via signup. See ROADMAP.md "Strategic Posture" for the pricing
  // decision and docs/TRIAL_AND_MARKETING_LAUNCH_PLAN.md for the trial flow.
  const plans = {
    sparvi: {
      name: 'Sparvi',
      price: '$999/mo',
      description: '14-day free trial. Full features. No credit card required for trial.',
      features: [
        'All Snowflake & BigQuery connections',
        'Unlimited users on your team',
        'Anomaly detection + schema monitoring',
        'Auto-generated validations + custom SQL rules',
        'Slack + email alerts with smart routing',
        '30-min setup, founder-led onboarding'
      ],
      icon: ChartBarIcon,
      color: 'indigo'
    }
  };

  const steps = [
    { number: 1, name: 'Plan', description: 'Start your trial' },
    { number: 2, name: 'Account', description: 'Create your account' },
    { number: 3, name: 'Organization', description: 'Set up your team' }
  ];

  // Organization validation with debouncing
  const validateOrganization = useCallback(async (organizationName) => {
    if (!organizationName.trim()) {
      setOrganizationValidation({
        isChecking: false,
        isValid: true,
        conflict: null,
        message: null
      });
      return;
    }

    setOrganizationValidation(prev => ({ ...prev, isChecking: true }));

    try {
      const result = await organizationValidationAPI.checkOrganizationExists(organizationName);
      
      setOrganizationValidation({
        isChecking: false,
        isValid: !result.exists,
        conflict: result.exists ? result.conflictType : null,
        message: result.message
      });
    } catch (error) {
      console.error('Organization validation error:', error);
      setOrganizationValidation({
        isChecking: false,
        isValid: true, // Don't block signup on validation error
        conflict: null,
        message: null
      });
    }
  }, []);

  // Email domain validation
  const validateEmailDomain = useCallback(async (email) => {
    if (!email.includes('@') || organizationUtils.isPublicEmailDomain(organizationUtils.getDomainFromEmail(email))) {
      setEmailDomainValidation({
        isChecking: false,
        hasConflict: false,
        organizationName: null,
        adminContact: null,
        message: null
      });
      return;
    }

    setEmailDomainValidation(prev => ({ ...prev, isChecking: true }));

    try {
      const result = await organizationValidationAPI.checkEmailDomain(email);
      
      setEmailDomainValidation({
        isChecking: false,
        hasConflict: result.hasConflict,
        organizationName: result.organizationName,
        adminContact: result.adminContact,
        message: result.message
      });
    } catch (error) {
      console.error('Email domain validation error:', error);
      setEmailDomainValidation({
        isChecking: false,
        hasConflict: false,
        organizationName: null,
        adminContact: null,
        message: null
      });
    }
  }, []);

  // Debounced validation effects
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.organizationName && currentStep >= 3) {
        validateOrganization(formData.organizationName);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [formData.organizationName, currentStep, validateOrganization]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.email && currentStep >= 2) {
        validateEmailDomain(formData.email);
      }
    }, 1000); // 1s debounce for email (less frequent)

    return () => clearTimeout(timeoutId);
  }, [formData.email, currentStep, validateEmailDomain]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    // Clear validation states when relevant fields change
    if (field === 'organizationName') {
      setOrganizationValidation({
        isChecking: false,
        isValid: true,
        conflict: null,
        message: null
      });
    }
    if (field === 'email') {
      setEmailDomainValidation({
        isChecking: false,
        hasConflict: false,
        organizationName: null,
        adminContact: null,
        message: null
      });
    }
  };

  // Validation functions
  const validateStep = (step) => {
    if (step === 2) {
      // Only validate the fields that are on step 2
      const step2ValidationSchema = {
        firstName: signUpValidationSchema.firstName,
        lastName: signUpValidationSchema.lastName,
        email: signUpValidationSchema.email,
        password: signUpValidationSchema.password,
        confirmPassword: signUpValidationSchema.confirmPassword
      };
      const { isValid, errors: validationErrors } = validateForm(formData, step2ValidationSchema);
      setErrors(validationErrors);
      return isValid;
    }

    if (step === 3) {
      // Check for organization conflicts first
      if (!organizationValidation.isValid) {
        setErrors({ 
          organizationName: 'This organization already has a Sparvi account. Please contact your admin for an invitation.' 
        });
        return false;
      }

      if (emailDomainValidation.hasConflict) {
        setErrors({ 
          email: `Your email domain belongs to ${emailDomainValidation.organizationName}. Please contact your admin for an invitation.` 
        });
        return false;
      }

      const orgValidationSchema = {
        organizationName: signUpValidationSchema.organizationName,
        agreeToTerms: signUpValidationSchema.agreeToTerms
      };
      const { isValid, errors: validationErrors } = validateForm(formData, orgValidationSchema);
      setErrors(validationErrors);
      return isValid;
    }

    // Step 1 (plan selection) doesn't need validation
    return true;
  };

  // Step navigation
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 3));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(3)) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Create account with organization
      await signUp({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        organizationName: formData.organizationName,
        teamSize: formData.teamSize,
        useCase: formData.useCase,
        selectedPlan: formData.selectedPlan,
        source: source
      });

      showNotification('Welcome to Sparvi. Your 14-day trial is active.', 'success');
      
      // Redirect to onboarding or dashboard
      navigate('/onboarding/welcome', { 
        state: { 
          newUser: true, 
          plan: formData.selectedPlan 
        } 
      });
      
    } catch (error) {
      console.error('Signup error:', error);
      const message = error.message || 'Failed to create account';
      setSubmitError(message);
      showNotification(message, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Password strength indicator
  const passwordStrengthData = getPasswordStrength(formData.password);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left side - Progress and branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-600 to-primary-800 p-12 flex-col justify-between">
        <div>
          <Logo className="h-8 text-white" />
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-white mb-4">
              Welcome to the future of data observability
            </h2>
            <p className="text-primary-100 text-lg">
              Built for modern data teams who need reliable, automated data quality monitoring without the complexity.
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="mt-16">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  currentStep >= step.number 
                    ? 'bg-white text-primary-600' 
                    : 'bg-primary-500 text-primary-100'
                }`}>
                  {currentStep > step.number ? (
                    <CheckCircleIcon className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-medium">{step.number}</span>
                  )}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p className={`text-sm font-medium ${
                    currentStep >= step.number ? 'text-white' : 'text-primary-200'
                  }`}>
                    {step.name}
                  </p>
                  <p className="text-xs text-primary-200">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 mx-4 h-0.5 ${
                    currentStep > step.number ? 'bg-white' : 'bg-primary-500'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 border-t border-primary-500 pt-8">
          <div className="flex items-center space-x-2 text-primary-100">
            <UsersIcon className="w-5 h-5" />
            <span className="text-sm">Built for data teams</span>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20">
        <div className="mx-auto w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 text-center">
            <Logo className="h-8 mx-auto" />
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {currentStep === 1 && 'Start Your 14-Day Trial'}
              {currentStep === 2 && 'Create Your Account'}
              {currentStep === 3 && 'Set Up Your Organization'}
            </h2>
            <p className="text-sm text-gray-600">
              {currentStep === 1 && 'Full access to Sparvi for 14 days. No credit card required for trial.'}
              {currentStep === 2 && 'Enter your details to create your Sparvi account'}
              {currentStep === 3 && 'Tell us about your team and use case'}
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {/* Step 1: Plan Selection */}
            {currentStep === 1 && (
              <div className="space-y-4">
                {Object.entries(plans).map(([planKey, plan]) => (
                  <div
                    key={planKey}
                    className={`relative border rounded-lg p-4 cursor-pointer transition-all ${
                      formData.selectedPlan === planKey
                        ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleInputChange('selectedPlan', planKey)}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <plan.icon className={`h-6 w-6 ${
                          formData.selectedPlan === planKey ? 'text-primary-600' : 'text-gray-400'
                        }`} />
                      </div>
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-medium text-gray-900">{plan.name}</h3>
                          <span className="text-lg font-bold text-gray-900">{plan.price}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                        <ul className="mt-3 space-y-1">
                          {plan.features.map((feature, index) => (
                            <li key={index} className="flex items-center text-sm text-gray-600">
                              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Step 2: Account Details */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      First name
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        errors.firstName ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="John"
                    />
                    {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Last name
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={`mt-1 block w-full border rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        errors.lastName ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Doe"
                    />
                    {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`mt-1 block w-full border rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="john@company.com"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className={`block w-full border rounded-md px-3 py-2 pr-10 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        errors.password ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {formData.password && passwordStrengthData.score > 0 && (
                    <div className="mt-2">
                      <div className="flex items-center space-x-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className={`h-1 w-full rounded ${
                              level <= passwordStrengthData.score
                                ? `bg-${passwordStrengthData.color}-500`
                                : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                      <p className={`text-xs mt-1 text-${passwordStrengthData.color}-600`}>
                        {passwordStrengthData.label}
                      </p>
                    </div>
                  )}
                  {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm password
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className={`block w-full border rounded-md px-3 py-2 pr-10 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
                </div>
              </div>
            )}

            {/* Step 3: Organization Setup */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div>
                  <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700">
                    Organization name
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="organizationName"
                      type="text"
                      value={formData.organizationName}
                      onChange={(e) => handleInputChange('organizationName', e.target.value)}
                      className={`block w-full border rounded-md px-3 py-2 pr-10 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                        errors.organizationName || !organizationValidation.isValid 
                          ? 'border-red-300' 
                          : organizationValidation.isValid && formData.organizationName && !organizationValidation.isChecking
                          ? 'border-green-300'
                          : 'border-gray-300'
                      }`}
                      placeholder="Acme Corp"
                    />
                    {/* Validation indicator */}
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      {organizationValidation.isChecking && (
                        <LoadingSpinner size="sm" />
                      )}
                      {!organizationValidation.isChecking && formData.organizationName && organizationValidation.isValid && (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      )}
                      {!organizationValidation.isChecking && !organizationValidation.isValid && (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                      )}
                    </div>
                  </div>
                  
                  {/* Organization validation messages */}
                  {!organizationValidation.isValid && organizationValidation.message && (
                    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-red-800">Organization Already Exists</h4>
                          <p className="text-sm text-red-700 mt-1">{organizationValidation.message}</p>
                          <div className="mt-2">
                            <p className="text-xs text-red-600">
                              If you work for this organization, please contact your admin to get an invitation instead of creating a new account.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Email domain conflict warning */}
                  {emailDomainValidation.hasConflict && currentStep === 3 && (
                    <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-md">
                      <div className="flex">
                        <InformationCircleIcon className="h-5 w-5 text-orange-400 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-medium text-orange-800">Email Domain Conflict</h4>
                          <p className="text-sm text-orange-700 mt-1">
                            Your email domain appears to belong to <strong>{emailDomainValidation.organizationName}</strong>.
                          </p>
                          {emailDomainValidation.adminContact && (
                            <p className="text-xs text-orange-600 mt-1">
                              Contact your admin ({emailDomainValidation.adminContact}) for an invitation.
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {errors.organizationName && <p className="mt-1 text-sm text-red-600">{errors.organizationName}</p>}
                </div>

                <div>
                  <label htmlFor="teamSize" className="block text-sm font-medium text-gray-700">
                    Team size (optional)
                  </label>
                  <select
                    id="teamSize"
                    value={formData.teamSize}
                    onChange={(e) => handleInputChange('teamSize', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select team size</option>
                    <option value="1">Just me</option>
                    <option value="2-10">2-10 people</option>
                    <option value="11-50">11-50 people</option>
                    <option value="51-200">51-200 people</option>
                    <option value="200+">200+ people</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="useCase" className="block text-sm font-medium text-gray-700">
                    Primary use case (optional)
                  </label>
                  <select
                    id="useCase"
                    value={formData.useCase}
                    onChange={(e) => handleInputChange('useCase', e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select use case</option>
                    <option value="data-quality">Data Quality Monitoring</option>
                    <option value="anomaly-detection">Anomaly Detection</option>
                    <option value="schema-monitoring">Schema Change Monitoring</option>
                    <option value="compliance">Data Compliance</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="flex items-start">
                  <input
                    id="agreeToTerms"
                    type="checkbox"
                    checked={formData.agreeToTerms}
                    onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-700">
                    I agree to the{' '}
                    <button
                      type="button"
                      onClick={() => setLegalModal('terms')}
                      className="text-primary-600 hover:text-primary-500 underline focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                    >
                      Terms of Service
                    </button>{' '}
                    and{' '}
                    <button
                      type="button"
                      onClick={() => setLegalModal('privacy')}
                      className="text-primary-600 hover:text-primary-500 underline focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                    >
                      Privacy Policy
                    </button>
                  </label>
                </div>
                {errors.agreeToTerms && <p className="text-sm text-red-600">{errors.agreeToTerms}</p>}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6">
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={prevStep}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Back
                </button>
              ) : (
                <div></div>
              )}

              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Continue
                </button>
              ) : (
                <div className="flex flex-col items-end gap-3">
                  {submitError && (
                    <div
                      role="alert"
                      className="w-full sm:w-96 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
                    >
                      {submitError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting || authLoading}
                    className="inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting || authLoading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Creating Account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </button>
                </div>
              )}
            </div>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center space-y-3">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                Sign in
              </Link>
            </p>
            
            {/* Organization signup notice */}
            <div className="bg-blue-50 rounded-lg p-3 text-left">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-800 font-medium">Organization Signup</p>
                  <p className="text-xs text-blue-700 mt-1">
                    This signup is for <strong>new organizations only</strong>. If your organization already uses Sparvi, 
                    ask your admin to invite you through the admin settings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legal docs shown in-modal so signup is not interrupted */}
      <LegalModal
        isOpen={legalModal === 'terms'}
        onClose={() => setLegalModal(null)}
        title="Terms of Service"
      >
        <TermsContent />
      </LegalModal>
      <LegalModal
        isOpen={legalModal === 'privacy'}
        onClose={() => setLegalModal(null)}
        title="Privacy Policy"
      >
        <PrivacyContent />
      </LegalModal>
    </div>
  );
};

export default SignUpPage;